import { useState } from "react";
import { Clock, Utensils, Footprints, MoonStar, CalendarDays, Plus, Trash2, X, MessageCircle, Sparkles, Check, Pencil, Wand2 } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { PerfectDayCelebration } from "./PerfectDayCelebration";
import { WeeklyRecapCard } from "./WeeklyRecapCard";
import { RhythmSetupModal } from "./RhythmSetupModal";
import { localDate } from "@/lib/dates";

const TYPE_META = {
  event: { icon: CalendarDays, chip: "Event" },
  movement: { icon: Footprints, chip: "Move 15 min" },
  meal: { icon: Utensils, chip: "Meal" },
  shutdown: { icon: MoonStar, chip: "Shutdown" },
};

const nowMinutes = () => {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
};

const toMinutes = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return h * 60 + m;
};

const fmtTime = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${suffix}`;
};

export const MyDayTab = ({ timeline, recap, personalized, dayMode = "weekday", onDayModeChange, completedToday = [], onCompleted, onRefresh, token, onDinnerConversation, onShutdownReflection, onMilestone, palette }) => {
  const [recipe, setRecipe] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ title: "", time: "12:00", type: "event", duration_min: 30 });
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const now = nowMinutes();
  const nextTwoHours = timeline.filter((t) => {
    const start = toMinutes(t.time);
    return start >= now - 10 && start <= now + 120;
  });

  const dinner = timeline.find((t) => t.type === "meal" && toMinutes(t.time) >= 17 * 60);
  const dinnerCountdown = dinner ? toMinutes(dinner.time) - now : null;

  const itemStatus = (t) => {
    const start = toMinutes(t.time);
    const end = start + (t.duration_min || 30);
    if (now >= start && now < end) return "now";
    return now >= end ? "past" : "upcoming";
  };

  const openRecipe = async (recipeId) => {
    try {
      const res = await axios.get(`${API}/rhythm/recipes/${recipeId}`, authHeaders);
      setRecipe(res.data);
    } catch (e) {
      toast.error("Couldn't load recipe steps");
    }
  };

  const addItem = async () => {
    if (!newItem.title.trim()) {
      toast.error("Give it a name first");
      return;
    }
    try {
      await axios.post(`${API}/rhythm/timeline`, { ...newItem, day_mode: dayMode }, authHeaders);
      toast.success("Added to your day");
      setShowAdd(false);
      setNewItem({ title: "", time: "12:00", type: "event", duration_min: 30 });
      onRefresh();
    } catch (e) {
      toast.error("Couldn't add item");
    }
  };

  const completeItem = async (id) => {
    if (completedToday.includes(id) || completing === id) return;
    setCompleting(id);
    try {
      const res = await axios.post(`${API}/rhythm/timeline/${id}/complete?date=${localDate()}`, {}, authHeaders);
      onCompleted(id);
      if (res.data.perfect_day) {
        setCelebration(res.data.bonus?.points_earned || 15);
      } else if (res.data.garden?.points_earned) {
        toast.success(`Done! +${res.data.garden.points_earned} — your garden grew 🌱`);
      } else {
        toast.success("Marked done ✓");
      }
      if (res.data.milestone) onMilestone(res.data.milestone);
    } catch (e) {
      toast.error("Couldn't mark that done");
    } finally {
      setCompleting(null);
    }
  };

  const saveEdit = async () => {
    if (!editItem.title.trim()) {
      toast.error("Give it a name first");
      return;
    }
    try {
      await axios.put(`${API}/rhythm/timeline/${editItem.id}`, {
        title: editItem.title,
        type: editItem.type,
        time: editItem.time,
        duration_min: editItem.duration_min || 30,
        recipe_id: editItem.recipe_id || null,
        day_mode: editItem.day_mode || dayMode,
      }, authHeaders);
      toast.success("Updated");
      setEditItem(null);
      onRefresh();
    } catch (e) {
      toast.error("Couldn't update item");
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/rhythm/timeline/${id}`, authHeaders);
      onRefresh();
    } catch (e) {
      toast.error("Couldn't remove item");
    }
  };

  return (
    <div className="space-y-4" data-testid="rhythm-myday-tab">
      {/* Next 2 hours banner */}
      <div className="rounded-2xl p-5 text-white" style={{ backgroundColor: palette.primary }} data-testid="next-two-hours-banner">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4" />
          <span className="text-xs font-bold uppercase tracking-widest opacity-90">Next 2 Hours</span>
        </div>
        {nextTwoHours.length > 0 ? (
          <div className="space-y-1.5">
            {nextTwoHours.slice(0, 3).map((t) => (
              <p key={t.id} className="text-sm font-medium">
                <span className="opacity-80">{fmtTime(t.time)}</span> · {t.title}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-sm opacity-90">Nothing scheduled — a clear stretch is yours. Breathe into it.</p>
        )}
        {dinnerCountdown !== null && dinnerCountdown > 0 && (
          <p className="text-xs mt-3 bg-white/15 rounded-full px-3 py-1.5 inline-block" data-testid="dinner-countdown">
            🍽 Dinner prep in {Math.floor(dinnerCountdown / 60) > 0 ? `${Math.floor(dinnerCountdown / 60)}h ` : ""}{dinnerCountdown % 60}m
          </p>
        )}
      </div>

      <WeeklyRecapCard recap={recap} palette={palette} />

      {/* Set up my day banner (weekday template only) */}
      {!personalized && dayMode === "weekday" && (
        <div className="rounded-2xl border-2 border-dashed p-4 flex items-center gap-3" style={{ borderColor: palette.primary, backgroundColor: `${palette.primary}0A` }} data-testid="setup-banner">
          <Wand2 className="w-5 h-5 flex-shrink-0" style={{ color: palette.primary }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold" style={{ color: palette.primary }}>Make this timeline yours</p>
            <p className="text-xs opacity-70">These are example times — answer 4 quick questions to build your real day</p>
          </div>
          <button
            onClick={() => setShowSetup(true)}
            className="text-xs font-bold px-4 py-2 rounded-full text-white flex-shrink-0"
            style={{ backgroundColor: palette.primary }}
            data-testid="setup-open-btn"
          >
            Set Up My Day
          </button>
        </div>
      )}

      {/* Weekday / Weekend mode toggle */}
      <div className="flex items-center gap-2" data-testid="day-mode-toggle">
        {["weekday", "weekend"].map((m) => {
          const active = dayMode === m;
          const isToday = ([0, 6].includes(new Date().getDay()) ? "weekend" : "weekday") === m;
          return (
            <button
              key={m}
              onClick={() => onDayModeChange(m)}
              className="text-xs font-bold px-4 py-2 rounded-full border transition-colors capitalize"
              style={{
                backgroundColor: active ? palette.primary : "#FFFFFF",
                color: active ? "#FFFFFF" : palette.primary,
                borderColor: active ? palette.primary : palette.border,
              }}
              data-testid={`day-mode-${m}`}
            >
              {m === "weekend" ? "🌿 Weekend" : "Weekday"}
              {isToday && <span className="ml-1 opacity-70 font-medium normal-case">(today)</span>}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-body" style={{ color: palette.primary }}>Today's Timeline</h2>
        <div className="flex gap-2">
          {personalized && (
            <button
              onClick={() => setShowSetup(true)}
              className="flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors"
              style={{ borderColor: palette.border, color: palette.primary, backgroundColor: "#FFFFFF" }}
              title="Rebuild timeline around new times"
              data-testid="setup-rerun-btn"
            >
              <Wand2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors"
            style={{ borderColor: palette.border, color: palette.primary, backgroundColor: "#FFFFFF" }}
            data-testid="add-timeline-btn"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="rounded-2xl border bg-white p-4 space-y-3" style={{ borderColor: palette.border }} data-testid="add-timeline-form">
          <input
            className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
            style={{ borderColor: palette.border }}
            placeholder="What's happening?"
            value={newItem.title}
            onChange={(e) => setNewItem((p) => ({ ...p, title: e.target.value }))}
            data-testid="timeline-title-input"
          />
          <div className="flex gap-2">
            <input
              type="time"
              className="rounded-xl border px-3 py-2 text-sm flex-1"
              style={{ borderColor: palette.border }}
              value={newItem.time}
              onChange={(e) => setNewItem((p) => ({ ...p, time: e.target.value }))}
              data-testid="timeline-time-input"
            />
            <select
              className="rounded-xl border px-3 py-2 text-sm flex-1 bg-white"
              style={{ borderColor: palette.border }}
              value={newItem.type}
              onChange={(e) => setNewItem((p) => ({ ...p, type: e.target.value }))}
              data-testid="timeline-type-select"
            >
              <option value="event">Event</option>
              <option value="movement">Movement</option>
              <option value="meal">Meal</option>
              <option value="shutdown">Shutdown</option>
            </select>
          </div>
          <button
            onClick={addItem}
            className="w-full rounded-xl py-2.5 text-sm font-bold text-white"
            style={{ backgroundColor: palette.primary }}
            data-testid="timeline-save-btn"
          >
            Add to My Day
          </button>
        </div>
      )}

      <div className="space-y-2.5">
        {timeline.map((t) => {
          const meta = TYPE_META[t.type] || TYPE_META.event;
          const Icon = meta.icon;
          const status = itemStatus(t);
          const done = completedToday.includes(t.id);
          if (editItem?.id === t.id) {
            return (
              <div key={t.id} className="rounded-2xl border-2 bg-white p-4 space-y-3" style={{ borderColor: palette.primary }} data-testid={`timeline-edit-form-${t.id}`}>
                <input
                  className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                  style={{ borderColor: palette.border }}
                  value={editItem.title}
                  onChange={(e) => setEditItem((p) => ({ ...p, title: e.target.value }))}
                  data-testid="timeline-edit-title"
                />
                <div className="flex gap-2">
                  <input
                    type="time"
                    className="rounded-xl border px-3 py-2 text-sm flex-1"
                    style={{ borderColor: palette.border }}
                    value={editItem.time}
                    onChange={(e) => setEditItem((p) => ({ ...p, time: e.target.value }))}
                    data-testid="timeline-edit-time"
                  />
                  <select
                    className="rounded-xl border px-2 py-2 text-sm flex-1 bg-white"
                    style={{ borderColor: palette.border }}
                    value={editItem.type}
                    onChange={(e) => setEditItem((p) => ({ ...p, type: e.target.value }))}
                    data-testid="timeline-edit-type"
                  >
                    <option value="event">Event</option>
                    <option value="movement">Movement</option>
                    <option value="meal">Meal</option>
                    <option value="shutdown">Shutdown</option>
                  </select>
                  <select
                    className="rounded-xl border px-2 py-2 text-sm bg-white"
                    style={{ borderColor: palette.border }}
                    value={editItem.duration_min}
                    onChange={(e) => setEditItem((p) => ({ ...p, duration_min: Number(e.target.value) }))}
                    data-testid="timeline-edit-duration"
                  >
                    {[15, 30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d}m</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={saveEdit}
                    className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white"
                    style={{ backgroundColor: palette.primary }}
                    data-testid="timeline-edit-save"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditItem(null)}
                    className="flex-1 rounded-xl py-2.5 text-sm font-bold border"
                    style={{ borderColor: palette.border, color: palette.primary }}
                    data-testid="timeline-edit-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div
              key={t.id}
              className={`rounded-2xl border bg-white p-4 flex items-start gap-3 transition-all duration-300 ${status === "past" && !done ? "opacity-50" : ""}`}
              style={{
                borderColor: done ? palette.primary : status === "now" ? palette.primary : palette.border,
                borderWidth: status === "now" || done ? 2 : 1,
                backgroundColor: done ? "#F2F7F3" : "#FFFFFF",
                boxShadow: done ? `0 0 0 3px ${palette.primary}22, 0 4px 14px ${palette.primary}2E` : "none",
              }}
              data-testid={`timeline-item-${t.type}`}
            >
              <button
                onClick={() => completeItem(t.id)}
                disabled={done}
                aria-label={done ? "Completed" : "Mark as done"}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 active:scale-90"
                style={{ backgroundColor: done ? palette.primary : `${palette.primary}15` }}
                data-testid={`timeline-complete-${t.id}`}
              >
                {done ? (
                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                ) : (
                  <Icon className="w-5 h-5" style={{ color: palette.primary }} />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold" style={{ color: palette.primary }}>{fmtTime(t.time)}</span>
                  {status === "now" && (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: palette.primary }}>
                      Now
                    </span>
                  )}
                  <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{ borderColor: palette.border, color: "#6B776F" }}>
                    {meta.chip}
                  </span>
                </div>
                <p className="text-sm font-medium mt-0.5">{t.title}</p>
                {!done && (
                  <button
                    onClick={() => completeItem(t.id)}
                    className="text-xs font-semibold mt-1.5 px-3 py-1 rounded-full border transition-colors"
                    style={{ borderColor: `${palette.primary}55`, color: palette.primary }}
                    data-testid={`timeline-done-btn-${t.id}`}
                  >
                    <Check className="w-3 h-3 inline mr-1" />
                    Done
                  </button>
                )}
                {done && (
                  <p className="text-xs font-semibold mt-1.5" style={{ color: palette.primary }} data-testid={`timeline-done-label-${t.id}`}>
                    ✓ Completed today
                  </p>
                )}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {t.type === "meal" && t.recipe_id && (
                    <button
                      onClick={() => openRecipe(t.recipe_id)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full text-white"
                      style={{ backgroundColor: palette.primary }}
                      data-testid="recipe-prep-btn"
                    >
                      <Utensils className="w-3 h-3 inline mr-1" />
                      Prep Steps
                    </button>
                  )}
                  {t.type === "meal" && toMinutes(t.time) >= 17 * 60 && (
                    <button
                      onClick={onDinnerConversation}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border"
                      style={{ borderColor: palette.primary, color: palette.primary }}
                      data-testid="dinner-conversation-btn"
                    >
                      <MessageCircle className="w-3 h-3 inline mr-1" />
                      Table Question
                    </button>
                  )}
                  {t.type === "shutdown" && (
                    <button
                      onClick={onShutdownReflection}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border"
                      style={{ borderColor: palette.primary, color: palette.primary }}
                      data-testid="shutdown-reflect-btn"
                    >
                      <Sparkles className="w-3 h-3 inline mr-1" />
                      Reflect
                    </button>
                  )}
                </div>
              </div>
              <button
                onClick={() => setEditItem({ ...t, duration_min: t.duration_min || 30 })}
                className="p-1.5 rounded-full hover:bg-black/5"
                aria-label="Edit item"
                data-testid={`timeline-edit-${t.id}`}
              >
                <Pencil className="w-4 h-4" style={{ color: "#9AA69E" }} />
              </button>
              <button
                onClick={() => deleteItem(t.id)}
                className="p-1.5 rounded-full hover:bg-black/5"
                aria-label="Remove item"
                data-testid={`timeline-delete-${t.id}`}
              >
                <Trash2 className="w-4 h-4" style={{ color: "#9AA69E" }} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Setup modal */}
      {showSetup && (
        <RhythmSetupModal token={token} onClose={() => setShowSetup(false)} onDone={onRefresh} palette={palette} />
      )}

      {/* Perfect Day celebration */}
      {celebration !== null && (
        <PerfectDayCelebration points={celebration} onClose={() => setCelebration(null)} palette={palette} />
      )}

      {/* Recipe modal */}
      {recipe && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40" onClick={() => setRecipe(null)}>
          <div
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="recipe-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold font-body" style={{ color: palette.primary }}>{recipe.name}</h3>
                <p className="text-xs opacity-70">~{recipe.prep_minutes} min prep</p>
              </div>
              <button onClick={() => setRecipe(null)} className="p-2 rounded-full hover:bg-black/5" data-testid="recipe-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ol className="space-y-3">
              {recipe.steps.map((step, i) => (
                <li key={step} className="flex gap-3 text-sm">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: palette.primary }}
                  >
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};
